import React, { useEffect, useRef, useState } from "react";
import {
  IconButton,
  Box,
  Avatar,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Input,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import AccountBoxOutlinedIcon from "@mui/icons-material/AccountBoxOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { toggleUserProfile } from "../../modules/profile/redux/profileSlice";
import { RootState, AppDispatch } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { editProfileThunk, fetchProfile } from "./redux/thunk/profileThunk";
import { useNavigate } from "react-router-dom";

const Profile = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isUserProfileOpen = useSelector(
    (state: RootState) => state.profile.isUserProfileOpen
  );
  const { profile, loading, error } = useSelector(
    (state: RootState) => state.profile
  );
  const navigate = useNavigate();

  function getFirstAndLastInitial(name: string): string {
    if (!name) return "";

    const words = name.trim().split(/\s+/); // split by spaces
    const first = words[0]?.[0]?.toUpperCase() || "";
    const last =
      words.length > 1 ? words[words.length - 1][0].toUpperCase() : "";

    return first + last;
  }

  // Always call hooks at the top level
  const [cin, setCin] = useState("");
  const [tin, setTin] = useState("");

  const fetchProfileData = async () => {
    const profileResult = await dispatch(fetchProfile()).unwrap(); 
    if (profileResult && profileResult.user_category !== 1 && profileResult.user_category !== 2) {
      navigate('view-profile');
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [dispatch]);

  // Optionally, update cin/tin when organizations change
  useEffect(() => {
    if (profile && profile.organizations && profile.organizations.length > 0) {
      setCin(profile.organizations[0]?.cin || "");
      setTin(profile.organizations[0]?.tin || "");
    }
  }, [profile]);

  if (!profile) return null;

  const {
    id,
    name,
    email,
    mobile_number,
    dob,
    pan_number,
    address,
    organizations,
  } = profile;

  if (!organizations || organizations.length === 0) {
    return null;
  } 

  return (
    <div>
      {isUserProfileOpen && (
        <Dialog
          open={isUserProfileOpen}
          onClose={() => dispatch(toggleUserProfile(false))}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle className="dialog-title flex items-center justify-between">
            User Profile
            <IconButton
              aria-label="close"
              onClick={() => dispatch(toggleUserProfile(false))}
              className="close-icon"
              sx={{
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#3A1C73"
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center"
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: "#3A1C7329",
                boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.12)",
                border: "1px solid #3A1C73",
                marginLeft: "1.5rem"
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#3A1C73",
                  fontSize: "1rem",
                  fontWeight: "normal"
                }}
              >
                {getFirstAndLastInitial(name)}
              </Typography>
            </Avatar>
            <DialogTitle className="dialog-title">{name}</DialogTitle>
          </Box>

          <DialogContent className="dialog-content">
            <Typography
              variant="body2"
              className="dialog-description"
              sx={{
                fontSize: "16px",
                fontWeight: "bold"
              }}
            >
              Individual Details
            </Typography>
            {profile && (
              <Box
                className="dialog-row"
                sx={{
                  flexWrap: "wrap",
                  justifyContent: "flex-start"
                }}
              >
                <Box className="dialog-row dialog-profile">
                  <svg
                    width="18"
                    height="20"
                    viewBox="0 0 18 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.30775 19.5C1.80258 19.5 1.375 19.325 1.025 18.975C0.675 18.625 0.5 18.1974 0.5 17.6922V4.30773C0.5 3.80257 0.675 3.37498 1.025 3.02498C1.375 2.67498 1.80258 2.49998 2.30775 2.49998H3.69225V0.384735H5.23075V2.49998H12.8077V0.384735H14.3077V2.49998H15.6923C16.1974 2.49998 16.625 2.67498 16.975 3.02498C17.325 3.37498 17.5 3.80257 17.5 4.30773V17.6922C17.5 18.1974 17.325 18.625 16.975 18.975C16.625 19.325 16.1974 19.5 15.6923 19.5H2.30775ZM2.30775 18H15.6923C15.7692 18 15.8398 17.9679 15.9038 17.9037C15.9679 17.8397 16 17.7692 16 17.6922V8.30773H2V17.6922C2 17.7692 2.03208 17.8397 2.09625 17.9037C2.16025 17.9679 2.23075 18 2.30775 18ZM2 6.80773H16V4.30773C16 4.23073 15.9679 4.16023 15.9038 4.09623C15.8398 4.03207 15.7692 3.99998 15.6923 3.99998H2.30775C2.23075 3.99998 2.16025 4.03207 2.09625 4.09623C2.03208 4.16023 2 4.23073 2 4.30773V6.80773ZM9 12.077C8.75517 12.077 8.5465 11.9907 8.374 11.8182C8.20167 11.6459 8.1155 11.4372 8.1155 11.1922C8.1155 10.9474 8.20167 10.7387 8.374 10.5662C8.5465 10.3939 8.75517 10.3077 9 10.3077C9.24483 10.3077 9.4535 10.3939 9.626 10.5662C9.79833 10.7387 9.8845 10.9474 9.8845 11.1922C9.8845 11.4372 9.79833 11.6459 9.626 11.8182C9.4535 11.9907 9.24483 12.077 9 12.077ZM5 12.077C4.75517 12.077 4.5465 11.9907 4.374 11.8182C4.20167 11.6459 4.1155 11.4372 4.1155 11.1922C4.1155 10.9474 4.20167 10.7387 4.374 10.5662C4.5465 10.3939 4.75517 10.3077 5 10.3077C5.24483 10.3077 5.4535 10.3939 5.626 10.5662C5.79833 10.7387 5.8845 10.9474 5.8845 11.1922C5.8845 11.4372 5.79833 11.6459 5.626 11.8182C5.4535 11.9907 5.24483 12.077 5 12.077ZM13 12.077C12.7552 12.077 12.5465 11.9907 12.374 11.8182C12.2017 11.6459 12.1155 11.4372 12.1155 11.1922C12.1155 10.9474 12.2017 10.7387 12.374 10.5662C12.5465 10.3939 12.7552 10.3077 13 10.3077C13.2448 10.3077 13.4535 10.3939 13.626 10.5662C13.7983 10.7387 13.8845 10.9474 13.8845 11.1922C13.8845 11.4372 13.7983 11.6459 13.626 11.8182C13.4535 11.9907 13.2448 12.077 13 12.077ZM9 16C8.75517 16 8.5465 15.9137 8.374 15.7412C8.20167 15.5689 8.1155 15.3603 8.1155 15.1155C8.1155 14.8705 8.20167 14.6618 8.374 14.4895C8.5465 14.317 8.75517 14.2307 9 14.2307C9.24483 14.2307 9.4535 14.317 9.626 14.4895C9.79833 14.6618 9.8845 14.8705 9.8845 15.1155C9.8845 15.3603 9.79833 15.5689 9.626 15.7412C9.4535 15.9137 9.24483 16 9 16ZM5 16C4.75517 16 4.5465 15.9137 4.374 15.7412C4.20167 15.5689 4.1155 15.3603 4.1155 15.1155C4.1155 14.8705 4.20167 14.6618 4.374 14.4895C4.5465 14.317 4.75517 14.2307 5 14.2307C5.24483 14.2307 5.4535 14.317 5.626 14.4895C5.79833 14.6618 5.8845 14.8705 5.8845 15.1155C5.8845 15.3603 5.79833 15.5689 5.626 15.7412C5.4535 15.9137 5.24483 16 5 16ZM13 16C12.7552 16 12.5465 15.9137 12.374 15.7412C12.2017 15.5689 12.1155 15.3603 12.1155 15.1155C12.1155 14.8705 12.2017 14.6618 12.374 14.4895C12.5465 14.317 12.7552 14.2307 13 14.2307C13.2448 14.2307 13.4535 14.317 13.626 14.4895C13.7983 14.6618 13.8845 14.8705 13.8845 15.1155C13.8845 15.3603 13.7983 15.5689 13.626 15.7412C13.4535 15.9137 13.2448 16 13 16Z"
                      fill="#1C1B1F"
                    />
                  </svg>
                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      Date of Birth
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {dob || "N/A"}
                    </Typography>
                  </Box>
                </Box>
                <Box className="dialog-row dialog-profile">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.4402 17.5C14.5557 17.5 12.6625 17.0618 10.7605 16.1855C8.85867 15.3092 7.11125 14.073 5.51825 12.477C3.92542 10.8808 2.69083 9.13333 1.8145 7.2345C0.938167 5.33583 0.5 3.44425 0.5 1.55975C0.5 1.25692 0.6 1.00458 0.8 0.80275C1 0.600916 1.25 0.5 1.55 0.5H4.8115C5.064 0.5 5.28675 0.582417 5.47975 0.74725C5.67275 0.911917 5.7955 1.11542 5.848 1.35775L6.42125 4.3C6.46092 4.573 6.45258 4.80758 6.39625 5.00375C6.33975 5.19992 6.23842 5.36467 6.09225 5.498L3.78275 7.74625C4.15442 8.42692 4.57908 9.07083 5.05675 9.678C5.53425 10.285 6.05125 10.8648 6.60775 11.4173C7.15642 11.9661 7.73975 12.4757 8.35775 12.9462C8.97575 13.4167 9.64308 13.8546 10.3598 14.2598L12.6038 11.9963C12.7603 11.8334 12.9498 11.7193 13.1723 11.6538C13.3946 11.5884 13.6257 11.5724 13.8655 11.6058L16.6423 12.1713C16.8948 12.2379 17.1008 12.3667 17.2605 12.5577C17.4202 12.7487 17.5 12.9654 17.5 13.2078V16.45C17.5 16.75 17.3991 17 17.1973 17.2C16.9954 17.4 16.7431 17.5 16.4402 17.5ZM3.073 6.327L4.85775 4.61925C4.88975 4.59358 4.91058 4.55833 4.92025 4.5135C4.92992 4.46867 4.92833 4.427 4.9155 4.3885L4.48075 2.15375C4.46792 2.10258 4.4455 2.06417 4.4135 2.0385C4.3815 2.01283 4.33983 2 4.2885 2H2.15C2.1115 2 2.07942 2.01283 2.05375 2.0385C2.02825 2.06417 2.0155 2.09625 2.0155 2.13475C2.06667 2.81808 2.1785 3.51225 2.351 4.21725C2.52333 4.92242 2.764 5.62567 3.073 6.327ZM11.773 14.9693C12.4358 15.2783 13.1272 15.5145 13.847 15.678C14.567 15.8413 15.2397 15.9384 15.8652 15.9693C15.9037 15.9693 15.9358 15.9564 15.9615 15.9307C15.9872 15.9051 16 15.873 16 15.8345V13.7308C16 13.6794 15.9872 13.6377 15.9615 13.6058C15.9358 13.5738 15.8974 13.5513 15.8462 13.5385L13.7462 13.1115C13.7077 13.0987 13.6741 13.0971 13.6453 13.1067C13.6164 13.1164 13.5859 13.1372 13.5538 13.1692L11.773 14.9693Z"
                      fill="#1C1B1F"
                    />
                  </svg>
                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      Mobile Number{" "}
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {mobile_number}
                    </Typography>
                  </Box>
                </Box>
                <Box className="dialog-row dialog-profile">
                  <svg
                    width="20"
                    height="16"
                    viewBox="0 0 20 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.30775 15.5C1.80258 15.5 1.375 15.325 1.025 14.975C0.675 14.625 0.5 14.1974 0.5 13.6923V2.30775C0.5 1.80258 0.675 1.375 1.025 1.025C1.375 0.675 1.80258 0.5 2.30775 0.5H17.6923C18.1974 0.5 18.625 0.675 18.975 1.025C19.325 1.375 19.5 1.80258 19.5 2.30775V13.6923C19.5 14.1974 19.325 14.625 18.975 14.975C18.625 15.325 18.1974 15.5 17.6923 15.5H2.30775ZM10 8.55775L2 3.44225V13.6923C2 13.7821 2.02883 13.8558 2.0865 13.9135C2.14417 13.9712 2.21792 14 2.30775 14H17.6923C17.7821 14 17.8558 13.9712 17.9135 13.9135C17.9712 13.8558 18 13.7821 18 13.6923V3.44225L10 8.55775ZM10 7L17.8463 2H2.15375L10 7ZM2 3.44225V2V13.6923C2 13.7821 2.02883 13.8558 2.0865 13.9135C2.14417 13.9712 2.21792 14 2.30775 14H2V3.44225Z"
                      fill="#1C1B1F"
                    />
                  </svg>
                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      Email
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {email}
                    </Typography>
                  </Box>
                </Box>
                <Box className="dialog-row dialog-profile">
                  <LocationOnOutlinedIcon fontSize="medium" />

                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      Address
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {address}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            <Typography
              variant="body2"
              className="dialog-description"
              sx={{
                fontSize: "16px",
                fontWeight: "bold"
              }}
            >
              Organisation Details
            </Typography>
            {profile && (
              <Box
                className="dialog-row"
                sx={{
                  flexWrap: "wrap",
                  justifyContent: "flex-start"
                }}
              >
                <Box className="dialog-row dialog-profile">
                  <AccountBalanceOutlinedIcon fontSize="medium" />
                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      Business Name
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {organizations[0]?.business_name || "N/A"}
                    </Typography>
                  </Box>
                </Box>
                <Box className="dialog-row dialog-profile">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.4402 17.5C14.5557 17.5 12.6625 17.0618 10.7605 16.1855C8.85867 15.3092 7.11125 14.073 5.51825 12.477C3.92542 10.8808 2.69083 9.13333 1.8145 7.2345C0.938167 5.33583 0.5 3.44425 0.5 1.55975C0.5 1.25692 0.6 1.00458 0.8 0.80275C1 0.600916 1.25 0.5 1.55 0.5H4.8115C5.064 0.5 5.28675 0.582417 5.47975 0.74725C5.67275 0.911917 5.7955 1.11542 5.848 1.35775L6.42125 4.3C6.46092 4.573 6.45258 4.80758 6.39625 5.00375C6.33975 5.19992 6.23842 5.36467 6.09225 5.498L3.78275 7.74625C4.15442 8.42692 4.57908 9.07083 5.05675 9.678C5.53425 10.285 6.05125 10.8648 6.60775 11.4173C7.15642 11.9661 7.73975 12.4757 8.35775 12.9462C8.97575 13.4167 9.64308 13.8546 10.3598 14.2598L12.6038 11.9963C12.7603 11.8334 12.9498 11.7193 13.1723 11.6538C13.3946 11.5884 13.6257 11.5724 13.8655 11.6058L16.6423 12.1713C16.8948 12.2379 17.1008 12.3667 17.2605 12.5577C17.4202 12.7487 17.5 12.9654 17.5 13.2078V16.45C17.5 16.75 17.3991 17 17.1973 17.2C16.9954 17.4 16.7431 17.5 16.4402 17.5ZM3.073 6.327L4.85775 4.61925C4.88975 4.59358 4.91058 4.55833 4.92025 4.5135C4.92992 4.46867 4.92833 4.427 4.9155 4.3885L4.48075 2.15375C4.46792 2.10258 4.4455 2.06417 4.4135 2.0385C4.3815 2.01283 4.33983 2 4.2885 2H2.15C2.1115 2 2.07942 2.01283 2.05375 2.0385C2.02825 2.06417 2.0155 2.09625 2.0155 2.13475C2.06667 2.81808 2.1785 3.51225 2.351 4.21725C2.52333 4.92242 2.764 5.62567 3.073 6.327ZM11.773 14.9693C12.4358 15.2783 13.1272 15.5145 13.847 15.678C14.567 15.8413 15.2397 15.9384 15.8652 15.9693C15.9037 15.9693 15.9358 15.9564 15.9615 15.9307C15.9872 15.9051 16 15.873 16 15.8345V13.7308C16 13.6794 15.9872 13.6377 15.9615 13.6058C15.9358 13.5738 15.8974 13.5513 15.8462 13.5385L13.7462 13.1115C13.7077 13.0987 13.6741 13.0971 13.6453 13.1067C13.6164 13.1164 13.5859 13.1372 13.5538 13.1692L11.773 14.9693Z"
                      fill="#1C1B1F"
                    />
                  </svg>
                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      Entity Type
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {organizations[0]?.entity_type || "N/A"}
                    </Typography>
                  </Box>
                </Box>
                <Box className="dialog-row dialog-profile">
                  <WorkOutlineOutlinedIcon fontSize="medium" />
                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      Nature of Business
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {organizations[0]?.business_nature || "N/A"}
                    </Typography>
                  </Box>
                </Box>

                <Box className="dialog-row dialog-profile">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.5155 17.5C4.11417 17.5 2.92792 17.0144 1.95675 16.0433C0.985583 15.0721 0.5 13.8858 0.5 12.4845C0.5 11.8962 0.600333 11.3244 0.801 10.7692C1.00167 10.2141 1.28917 9.71092 1.6635 9.25975L5.19425 5.0135L2.94225 0.5H15.0577L12.8057 5.0135L16.3365 9.25975C16.7108 9.71092 16.9983 10.2141 17.199 10.7692C17.3997 11.3244 17.5 11.8962 17.5 12.4845C17.5 13.8858 17.0118 15.0721 16.0355 16.0433C15.0593 17.0144 13.8757 17.5 12.4845 17.5H5.5155ZM9 12.7402C8.50133 12.7402 8.07542 12.5637 7.72225 12.2105C7.36892 11.8573 7.19225 11.4314 7.19225 10.9327C7.19225 10.4339 7.36892 10.0079 7.72225 9.65475C8.07542 9.30158 8.50133 9.125 9 9.125C9.49867 9.125 9.92458 9.30158 10.2778 9.65475C10.6311 10.0079 10.8077 10.4339 10.8077 10.9327C10.8077 11.4314 10.6311 11.8573 10.2778 12.2105C9.92458 12.5637 9.49867 12.7402 9 12.7402ZM6.56725 4.375H11.4423L12.625 2H5.375L6.56725 4.375ZM5.5155 16H12.4845C13.4667 16 14.2981 15.658 14.9788 14.974C15.6596 14.29 16 13.4602 16 12.4845C16 12.0717 15.9292 11.6714 15.7875 11.2838C15.6458 10.8959 15.4436 10.5456 15.1807 10.2327L11.5538 5.875H6.47125L2.82875 10.2172C2.56592 10.5301 2.36208 10.883 2.21725 11.276C2.07242 11.6688 2 12.0717 2 12.4845C2 13.4602 2.342 14.29 3.026 14.974C3.71 15.658 4.53983 16 5.5155 16Z"
                      fill="#1C1B1F"
                    />
                  </svg>
                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      GST No:
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {organizations[0]?.gst_number || "N/A"}
                    </Typography>
                  </Box>
                </Box>
                <Box className="dialog-row dialog-profile">
                  <LocationOnOutlinedIcon fontSize="medium" />
                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      Address
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {`${organizations[0]?.city_name},${organizations[0].district_name} , ${organizations[0].state_name}` ||
                        "N/A"}
                    </Typography>
                  </Box>
                </Box>
                <Box className="dialog-row dialog-profile">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <mask
                      id="mask0_74799_86506"
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="24"
                      height="24"
                    >
                      <rect width="24" height="24" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_74799_86506)">
                      <path
                        d="M16.0898 11.3301C17.1628 11.3301 18.0911 11.7049 18.874 12.4541C19.6565 13.2033 20.0478 14.1828 20.0479 15.3926C20.0479 15.8128 19.9596 16.2479 19.7832 16.6973C19.6068 17.1467 19.3675 17.6017 19.0664 18.0625C18.7651 18.5234 18.4101 18.9836 18 19.4414C17.5899 19.8993 17.1493 20.3448 16.6787 20.7783C16.5973 20.8523 16.5041 20.9074 16.4004 20.9443C16.2969 20.9812 16.1934 21 16.0898 21C15.9877 21 15.8853 20.9825 15.7832 20.9473C15.681 20.912 15.5885 20.8571 15.5059 20.7832C15.0353 20.3495 14.5947 19.9029 14.1846 19.4434C13.7745 18.9839 13.4181 18.5246 13.1152 18.0654C12.8124 17.6063 12.5729 17.1512 12.3965 16.7002C12.22 16.249 12.1309 15.8128 12.1309 15.3926C12.1309 14.1826 12.523 13.2033 13.3057 12.4541C14.0886 11.7048 15.0168 11.3301 16.0898 11.3301ZM12.1338 3C12.3394 3.00002 12.5367 3.03165 12.7256 3.09473C12.9144 3.15796 13.0954 3.25266 13.2686 3.37891L19.2715 7.90918C19.4206 8.01668 19.5357 8.15692 19.6172 8.3291C19.6984 8.50113 19.7392 8.68364 19.7393 8.87695V9.04883C19.7393 9.2689 19.6644 9.45378 19.5137 9.60254C19.3629 9.75134 19.1758 9.82512 18.9531 9.8252C18.8377 9.8252 18.7339 9.80127 18.6426 9.75293C18.5512 9.70451 18.4786 9.63807 18.4248 9.55469V9.39355C18.4248 9.34 18.4122 9.29167 18.3887 9.24805C18.3652 9.20435 18.3333 9.16557 18.293 9.13184L12.3232 4.64355C12.2695 4.59664 12.2056 4.57331 12.1318 4.57324C12.0579 4.57324 11.9933 4.59653 11.9395 4.64355L5.96973 9.13184C5.92943 9.16554 5.89741 9.20441 5.87402 9.24805C5.85056 9.29164 5.83888 9.34006 5.83887 9.39355V18.3486C5.83887 18.4429 5.86921 18.5206 5.92969 18.5811C5.99009 18.6414 6.06711 18.6718 6.16113 18.6719H10.3262C10.549 18.6719 10.7359 18.7476 10.8867 18.8984C11.0373 19.0493 11.1133 19.2362 11.1133 19.459C11.1132 19.6818 11.0372 19.8682 10.8867 20.0186C10.7359 20.1692 10.549 20.2451 10.3262 20.2451H6.16113C5.63988 20.2451 5.19346 20.0596 4.82227 19.6885C4.45098 19.3172 4.26562 18.8701 4.26562 18.3486V9.39355C4.26563 9.09353 4.33264 8.80942 4.4668 8.54102C4.6012 8.27238 4.78662 8.0507 5.02344 7.87695L10.9941 3.37891C11.1682 3.25259 11.3503 3.15798 11.54 3.09473C11.73 3.03163 11.9281 3 12.1338 3ZM16.0889 14.3447C15.828 14.3447 15.6051 14.4383 15.4199 14.624C15.2346 14.8098 15.1416 15.033 15.1416 15.2939C15.1416 15.5547 15.2343 15.7778 15.4199 15.9629C15.6057 16.1481 15.8298 16.2412 16.0908 16.2412C16.3516 16.2411 16.5746 16.1477 16.7598 15.9619C16.9449 15.7761 17.0381 15.5528 17.0381 15.292C17.038 15.0312 16.9445 14.8083 16.7588 14.623C16.573 14.4379 16.3498 14.3448 16.0889 14.3447Z"
                        fill="#1C1B1F"
                      />
                    </g>
                  </svg>

                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      City
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {organizations[0]?.city_name || "N/A"}
                    </Typography>
                  </Box>
                </Box>
                <Box className="dialog-row dialog-profile">
                  <MapOutlinedIcon fontSize="medium" />
                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      State
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {organizations[0]?.state_name || "N/A"}
                    </Typography>
                  </Box>
                </Box>

                <Box className="dialog-row dialog-profile">
                  <svg
                    width="20"
                    height="18"
                    viewBox="0 0 20 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0.367188 17.577V0.0769958H9.86694V4.077H19.6362V17.577H0.367188ZM1.86694 16.077H4.36719V13.577H1.86694V16.077ZM1.86694 12.077H4.36719V9.57699H1.86694V12.077ZM1.86694 8.077H4.36719V5.577H1.86694V8.077ZM1.86694 4.077H4.36719V1.577H1.86694V4.077ZM5.86694 16.077H8.36719V13.577H5.86694V16.077ZM5.86694 12.077H8.36719V9.57699H5.86694V12.077ZM5.86694 8.077H8.36719V5.577H5.86694V8.077ZM5.86694 4.077H8.36719V1.577H5.86694V4.077ZM9.86694 16.077H18.1364V5.577H9.86694V8.077H12.0594V9.57699H9.86694V12.077H12.0594V13.577H9.86694V16.077ZM14.2517 9.57699V8.077H15.7517V9.57699H14.2517ZM14.2517 13.577V12.077H15.7517V13.577H14.2517Z"
                      fill="#1C1B1F"
                    />
                  </svg>
                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      Pincode
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {organizations[0]?.pincode || "N/A"}
                    </Typography>
                  </Box>
                </Box>
                <Box className="dialog-row dialog-profile">
                  <svg
                    width="18"
                    height="20"
                    viewBox="0 0 18 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.30775 19.5C1.80258 19.5 1.375 19.325 1.025 18.975C0.675 18.625 0.5 18.1974 0.5 17.6922V4.30773C0.5 3.80257 0.675 3.37498 1.025 3.02498C1.375 2.67498 1.80258 2.49998 2.30775 2.49998H3.69225V0.384735H5.23075V2.49998H12.8077V0.384735H14.3077V2.49998H15.6923C16.1974 2.49998 16.625 2.67498 16.975 3.02498C17.325 3.37498 17.5 3.80257 17.5 4.30773V17.6922C17.5 18.1974 17.325 18.625 16.975 18.975C16.625 19.325 16.1974 19.5 15.6923 19.5H2.30775ZM2.30775 18H15.6923C15.7692 18 15.8398 17.9679 15.9038 17.9037C15.9679 17.8397 16 17.7692 16 17.6922V8.30773H2V17.6922C2 17.7692 2.03208 17.8397 2.09625 17.9037C2.16025 17.9679 2.23075 18 2.30775 18ZM2 6.80773H16V4.30773C16 4.23073 15.9679 4.16023 15.9038 4.09623C15.8398 4.03207 15.7692 3.99998 15.6923 3.99998H2.30775C2.23075 3.99998 2.16025 4.03207 2.09625 4.09623C2.03208 4.16023 2 4.23073 2 4.30773V6.80773ZM9 12.077C8.75517 12.077 8.5465 11.9907 8.374 11.8182C8.20167 11.6459 8.1155 11.4372 8.1155 11.1922C8.1155 10.9474 8.20167 10.7387 8.374 10.5662C8.5465 10.3939 8.75517 10.3077 9 10.3077C9.24483 10.3077 9.4535 10.3939 9.626 10.5662C9.79833 10.7387 9.8845 10.9474 9.8845 11.1922C9.8845 11.4372 9.79833 11.6459 9.626 11.8182C9.4535 11.9907 9.24483 12.077 9 12.077ZM5 12.077C4.75517 12.077 4.5465 11.9907 4.374 11.8182C4.20167 11.6459 4.1155 11.4372 4.1155 11.1922C4.1155 10.9474 4.20167 10.7387 4.374 10.5662C4.5465 10.3939 4.75517 10.3077 5 10.3077C5.24483 10.3077 5.4535 10.3939 5.626 10.5662C5.79833 10.7387 5.8845 10.9474 5.8845 11.1922C5.8845 11.4372 5.79833 11.6459 5.626 11.8182C5.4535 11.9907 5.24483 12.077 5 12.077ZM13 12.077C12.7552 12.077 12.5465 11.9907 12.374 11.8182C12.2017 11.6459 12.1155 11.4372 12.1155 11.1922C12.1155 10.9474 12.2017 10.7387 12.374 10.5662C12.5465 10.3939 12.7552 10.3077 13 10.3077C13.2448 10.3077 13.4535 10.3939 13.626 10.5662C13.7983 10.7387 13.8845 10.9474 13.8845 11.1922C13.8845 11.4372 13.7983 11.6459 13.626 11.8182C13.4535 11.9907 13.2448 12.077 13 12.077ZM9 16C8.75517 16 8.5465 15.9137 8.374 15.7412C8.20167 15.5689 8.1155 15.3603 8.1155 15.1155C8.1155 14.8705 8.20167 14.6618 8.374 14.4895C8.5465 14.317 8.75517 14.2307 9 14.2307C9.24483 14.2307 9.4535 14.317 9.626 14.4895C9.79833 14.6618 9.8845 14.8705 9.8845 15.1155C9.8845 15.3603 9.79833 15.5689 9.626 15.7412C9.4535 15.9137 9.24483 16 9 16ZM5 16C4.75517 16 4.5465 15.9137 4.374 15.7412C4.20167 15.5689 4.1155 15.3603 4.1155 15.1155C4.1155 14.8705 4.20167 14.6618 4.374 14.4895C4.5465 14.317 4.75517 14.2307 5 14.2307C5.24483 14.2307 5.4535 14.317 5.626 14.4895C5.79833 14.6618 5.8845 14.8705 5.8845 15.1155C5.8845 15.3603 5.79833 15.5689 5.626 15.7412C5.4535 15.9137 5.24483 16 5 16ZM13 16C12.7552 16 12.5465 15.9137 12.374 15.7412C12.2017 15.5689 12.1155 15.3603 12.1155 15.1155C12.1155 14.8705 12.2017 14.6618 12.374 14.4895C12.5465 14.317 12.7552 14.2307 13 14.2307C13.2448 14.2307 13.4535 14.317 13.626 14.4895C13.7983 14.6618 13.8845 14.8705 13.8845 15.1155C13.8845 15.3603 13.7983 15.5689 13.626 15.7412C13.4535 15.9137 13.2448 16 13 16Z"
                      fill="#1C1B1F"
                    />
                  </svg>
                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      Registration Date
                    </Typography>
                    <Typography className="dialog-profile-field">
                      {organizations[0]?.registration_date || "N/A"}
                    </Typography>
                  </Box>
                </Box>
                <Box className="dialog-row dialog-profile">
                  <AccountBoxOutlinedIcon fontSize="medium" />
                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      CIN
                    </Typography>
                    <Input
                      placeholder="Enter CIN Number"
                      value={cin}
                      onChange={e => setCin(e.target.value)}
                    />
                  </Box>
                </Box>
                <Box className="dialog-row dialog-profile">
                  <svg
                    width="20"
                    height="14"
                    viewBox="0 0 20 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.75 7.75H3.84625C3.67442 7.75 3.53208 7.80642 3.41925 7.91925C3.30642 8.03208 3.25 8.17442 3.25 8.34625C3.25 8.51792 3.30642 8.66017 3.41925 8.773C3.53208 8.88583 3.67442 8.94225 3.84625 8.94225H6.05775C6.30258 8.94225 6.51125 8.85608 6.68375 8.68375C6.85608 8.51125 6.94225 8.30258 6.94225 8.05775V6.38475C6.94225 6.13975 6.85608 5.93108 6.68375 5.75875C6.51125 5.58625 6.30258 5.5 6.05775 5.5H4.44225V4.44225H6.34625C6.51792 4.44225 6.66017 4.38583 6.773 4.273C6.88583 4.16017 6.94225 4.01792 6.94225 3.84625C6.94225 3.67442 6.88583 3.53208 6.773 3.41925C6.66017 3.30642 6.51792 3.25 6.34625 3.25H3.84625C3.67442 3.25 3.53208 3.30642 3.41925 3.41925C3.30642 3.53208 3.25 3.67442 3.25 3.84625V6.09625C3.25 6.26792 3.30642 6.41017 3.41925 6.523C3.53208 6.63583 3.67442 6.69225 3.84625 6.69225H5.75V7.75ZM13 7.9615C11.8833 7.9615 10.9952 8.15192 10.3355 8.53275C9.676 8.91342 9.34625 9.43708 9.34625 10.1038V10.202C9.34625 10.3225 9.39142 10.4279 9.48175 10.5183C9.57208 10.6086 9.6775 10.6538 9.798 10.6538H16.202C16.3225 10.6538 16.4279 10.6086 16.5182 10.5183C16.6086 10.4279 16.6538 10.3225 16.6538 10.202V10.1038C16.6538 9.43708 16.324 8.91342 15.6645 8.53275C15.0048 8.15192 14.1167 7.9615 13 7.9615ZM13 6.84625C13.4858 6.84625 13.899 6.676 14.2395 6.3355C14.5798 5.99517 14.75 5.58208 14.75 5.09625C14.75 4.61025 14.5798 4.19708 14.2395 3.85675C13.899 3.51642 13.4858 3.34625 13 3.34625C12.5142 3.34625 12.101 3.51642 11.7605 3.85675C11.4202 4.19708 11.25 4.61025 11.25 5.09625C11.25 5.58208 11.4202 5.99517 11.7605 6.3355C12.101 6.676 12.5142 6.84625 13 6.84625ZM2.30775 13.5C1.80258 13.5 1.375 13.325 1.025 12.975C0.675 12.625 0.5 12.1974 0.5 11.6923V2.30775C0.5 1.80258 0.675 1.375 1.025 1.025C1.375 0.675 1.80258 0.5 2.30775 0.5H17.6923C18.1974 0.5 18.625 0.675 18.975 1.025C19.325 1.375 19.5 1.80258 19.5 2.30775V11.6923C19.5 12.1974 19.325 12.625 18.975 12.975C18.625 13.325 18.1974 13.5 17.6923 13.5H2.30775ZM2.30775 12H17.6923C17.7693 12 17.8398 11.9679 17.9038 11.9038C17.9679 11.8398 18 11.7692 18 11.6923V2.30775C18 2.23075 17.9679 2.16025 17.9038 2.09625C17.8398 2.03208 17.7693 2 17.6923 2H2.30775C2.23075 2 2.16025 2.03208 2.09625 2.09625C2.03208 2.16025 2 2.23075 2 2.30775V11.6923C2 11.7692 2.03208 11.8398 2.09625 11.9038C2.16025 11.9679 2.23075 12 2.30775 12Z"
                      fill="#1C1B1F"
                    />
                  </svg>

                  <Box sx={{ marginLeft: "1.5rem" }}>
                    <Typography className="dialog-profile-label">
                      TIN
                    </Typography>
                    <Input
                      placeholder="Enter TIN Number"
                      value={tin}
                      onChange={e => setTin(e.target.value)}
                    />
                  </Box>
                </Box>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "1rem"
              }}
            >
              <Button
                onClick={async () => {
                  await dispatch(
                    editProfileThunk({
                      id,
                      cin,
                      tin
                    })
                  );
                  dispatch(toggleUserProfile(false));
                }}
                variant="contained"
                className="stepper-next-btn"
              >
                Save
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Profile;
